import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { CheckCircle, Search } from 'lucide-react';

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:28 },
  card: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:14, padding:28, maxWidth:560 },
  label: { fontSize:11, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:5, display:'block' },
  input: { width:'100%', padding:'10px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:16, boxSizing:'border-box' },
  select: { width:'100%', padding:'10px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13, outline:'none', marginBottom:16 },
  row2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  btn: (c='#f6c90e') => ({ padding:'11px 20px', borderRadius:8, border:`1px solid ${c}44`, background:`${c}22`, color:c, fontSize:14, fontWeight:700, cursor:'pointer' }),
  prazoBox: { background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.2)', borderRadius:10, padding:16, marginBottom:16 },
  success: { background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:12, padding:24, textAlign:'center' },
  sectionTitle: { fontSize:11, color:'#2dd4bf', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10, marginTop:4, fontWeight:700 },
};

const ESTADOS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const EMPTY = {
  nome:'', telefone:'', cpf_doc:'', rg_doc:'',
  endereco:'', cidade:'', estado:'',
  consumo_mensal:'', como_foi_conversa:'',
  vendedor_id: null,
  prazo_resposta_vendedor:'',
  nota_vendedor: '',
};

const PERFIL_LABEL = { 1:'Chefe', 2:'Vendedor', 3:'Gerente Comercial' };

function formatarData(iso) {
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default function PreVendaLead() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [nomeDuplicado, setNomeDuplicado] = useState(null);
  const nomeCheckTimer = useRef(null);

  // Responsáveis (perfis 1, 2, 3)
  const [responsaveis, setResponsaveis] = useState([]);
  const [vendedorSearch, setVendedorSearch] = useState('');
  const [vendedorNome, setVendedorNome] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    api.get('/responsaveis/').then(r => setResponsaveis(r.data)).catch(() => {});
  }, []);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredResp = responsaveis.filter(u =>
    u.nome.toLowerCase().includes(vendedorSearch.toLowerCase()) ||
    PERFIL_LABEL[u.perfil_acesso]?.toLowerCase().includes(vendedorSearch.toLowerCase())
  );

  const selectVendedor = (u) => {
    setForm(p => ({ ...p, vendedor_id: u.id }));
    setVendedorNome(u.nome);
    setVendedorSearch('');
    setShowDropdown(false);
  };

  const clearVendedor = () => {
    setForm(p => ({ ...p, vendedor_id: null, prazo_resposta_vendedor: '', nota_vendedor: '' }));
    setVendedorNome('');
    setVendedorSearch('');
  };

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const checkNomeDuplicado = (nome) => {
    if (nomeCheckTimer.current) clearTimeout(nomeCheckTimer.current);
    if (nome.trim().length < 3) { setNomeDuplicado(null); return; }
    nomeCheckTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/pre-venda/verificar-nome/', { params: { nome: nome.trim() } });
        if (r.data.encontrado) {
          setNomeDuplicado({ nome, dataFormatada: formatarData(r.data.data_criacao) });
        } else {
          setNomeDuplicado(null);
        }
      } catch {
        setNomeDuplicado(null);
      }
    }, 600);
  };

  const onChangeNome = (e) => {
    const nome = e.target.value;
    setForm(p => ({ ...p, nome }));
    checkNomeDuplicado(nome);
  };

  const submit = async () => {
    if (!form.nome || !form.telefone) { setError('Nome e telefone são obrigatórios.'); return; }
    if (form.vendedor_id && !form.prazo_resposta_vendedor) {
      setError('Informe o prazo para o responsável entrar em contato.'); return;
    }
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        passar_para_vendedor: !!form.vendedor_id,
        prazo_resposta_vendedor: form.vendedor_id ? form.prazo_resposta_vendedor : null,
        nota_vendedor: form.vendedor_id ? form.nota_vendedor : '',
      };
      const r = await api.post('/leads/pre-venda/', payload);
      setSuccess(r.data);
      setForm(EMPTY);
      setVendedorNome('');
      setNomeDuplicado(null);
    } catch (e) {
      setError('Erro ao cadastrar: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setLoading(false); }
  };

  if (success) return (
    <div>
      <div style={S.heading}>Cadastrar Lead</div>
      <div style={S.sub}>Pré atendimento de novos clientes</div>
      <div style={{ ...S.success, maxWidth:480 }}>
        <CheckCircle size={40} color="#4ade80" style={{ margin:'0 auto 12px', display:'block' }} />
        <div style={{ fontSize:16, fontWeight:700, color:'#4ade80', marginBottom:8 }}>Lead cadastrado com sucesso!</div>
        <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
          Cliente: <strong style={{ color:'#e2e8f0' }}>{success.cliente_nome}</strong>
          {success.vendedor_nome && <><br />Designado para: <strong style={{ color:'#f6c90e' }}>{success.vendedor_nome}</strong></>}
        </div>
        <button style={S.btn()} onClick={() => setSuccess(null)}>Cadastrar novo lead</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={S.heading}>Cadastrar Lead</div>
      <div style={S.sub}>Pré atendimento de novos clientes</div>

      <div style={S.card}>
        {/* Dados básicos */}
        <div style={S.sectionTitle}>Dados do Cliente</div>

        <label style={S.label}>Nome do cliente *</label>
        <input style={S.input} value={form.nome} onChange={onChangeNome} placeholder="Nome completo" />
        {nomeDuplicado && (
          <div style={{ fontSize:12, color:'#f87171', marginTop:-10, marginBottom:16 }}>
            {nomeDuplicado.nome} já existe no sistema. Foi criado dia {nomeDuplicado.dataFormatada}. Recomendamos criar com outro nome.
          </div>
        )}

        <label style={S.label}>Telefone *</label>
        <input style={S.input} value={form.telefone} onChange={f('telefone')} placeholder="(34) 9-0000-0000" />

        <div style={S.row2}>
          <div>
            <label style={S.label}>CPF / CNPJ</label>
            <input style={S.input} value={form.cpf_doc} onChange={f('cpf_doc')} placeholder="000.000.000-00" />
          </div>
          <div>
            <label style={S.label}>RG</label>
            <input style={S.input} value={form.rg_doc} onChange={f('rg_doc')} placeholder="MG-00.000.000" />
          </div>
        </div>

        {/* Endereço completo */}
        <div style={S.sectionTitle}>Endereço</div>

        <label style={S.label}>Endereço (Rua, Número, Bairro)</label>
        <input style={S.input} value={form.endereco} onChange={f('endereco')} placeholder="Rua das Flores, 123, Centro" />

        <div style={S.row2}>
          <div>
            <label style={S.label}>Cidade</label>
            <input style={S.input} value={form.cidade} onChange={f('cidade')} placeholder="Uberlândia" />
          </div>
          <div>
            <label style={S.label}>Estado</label>
            <select style={S.select} value={form.estado} onChange={f('estado')}>
              <option value="">Selecione</option>
              {ESTADOS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
        </div>

        {/* Outros */}
        <div style={S.sectionTitle}>Informações da Conversa</div>

        <label style={S.label}>Consumo mensal de energia (R$)</label>
        <input style={S.input} type="number" value={form.consumo_mensal} onChange={f('consumo_mensal')} placeholder="350.00" />

        <label style={S.label}>Como foi a conversa</label>
        <textarea style={{ ...S.input, height:90, resize:'vertical' }} value={form.como_foi_conversa} onChange={f('como_foi_conversa')} placeholder="Descreva como foi o contato inicial com o cliente..." />

        {/* Designar responsável */}
        <div style={S.sectionTitle}>Designar Responsável</div>

        <label style={S.label}>Responsável pelo lead (opcional)</label>
        <div ref={dropRef} style={{ position:'relative', marginBottom:16 }}>
          {form.vendedor_id ? (
            <div style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(246,201,14,0.06)', border:'1px solid rgba(246,201,14,0.3)', borderRadius:8, padding:'10px 12px' }}>
              <span style={{ flex:1, fontSize:13, color:'#f6c90e', fontWeight:600 }}>{vendedorNome}</span>
              <button onClick={clearVendedor} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:12, padding:'2px 6px' }}>✕ Remover</button>
            </div>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:8, background:'#0f1117', border:'1px solid #1a2035', borderRadius:8, padding:'10px 12px' }}>
                <Search size={14} color="#475569" />
                <input
                  style={{ flex:1, background:'none', border:'none', color:'#e2e8f0', fontSize:13, outline:'none' }}
                  placeholder="Digite para buscar vendedor, chefe ou gerente..."
                  value={vendedorSearch}
                  onChange={e => { setVendedorSearch(e.target.value); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                />
              </div>
              {showDropdown && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#0e1420', border:'1px solid #1a2035', borderRadius:8, zIndex:100, maxHeight:200, overflowY:'auto', marginTop:2 }}>
                  {filteredResp.length === 0 ? (
                    <div style={{ padding:'10px 14px', fontSize:13, color:'#475569' }}>Nenhum resultado</div>
                  ) : filteredResp.map(u => (
                    <div
                      key={u.id}
                      onClick={() => selectVendedor(u)}
                      style={{ padding:'10px 14px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #0f1117' }}
                      onMouseEnter={e => e.currentTarget.style.background='#111827'}
                      onMouseLeave={e => e.currentTarget.style.background=''}
                    >
                      <span style={{ fontSize:13, color:'#e2e8f0' }}>{u.nome}</span>
                      <span style={{ fontSize:11, color:'#475569' }}>{PERFIL_LABEL[u.perfil_acesso]}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {form.vendedor_id && (
          <div style={S.prazoBox}>
            <label style={{ ...S.label, color:'#f6c90e' }}>Prazo para entrar em contato *</label>
            <input
              style={{ ...S.input, marginBottom:12, border:'1px solid rgba(246,201,14,0.3)' }}
              type="datetime-local"
              value={form.prazo_resposta_vendedor}
              onChange={f('prazo_resposta_vendedor')}
            />
            <label style={{ ...S.label, color:'#94a3b8', marginBottom:5 }}>Nota para o vendedor (opcional)</label>
            <textarea
              style={{ ...S.input, height:80, resize:'vertical', marginBottom:0, border:'1px solid rgba(246,201,14,0.15)' }}
              placeholder="Escreva uma nota para o responsável..."
              value={form.nota_vendedor}
              onChange={f('nota_vendedor')}
            />
            <div style={{ fontSize:11, color:'#64748b', marginTop:6 }}>
              Esta nota ficará registrada na aba Nota Vendedor.
            </div>
          </div>
        )}

        {error && <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:16 }}>{error}</div>}

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
          <button style={S.btn()} onClick={submit} disabled={loading}>
            {loading ? 'Cadastrando...' : 'Cadastrar Lead'}
          </button>
        </div>
      </div>
    </div>
  );
}
